/**
 * Combo performance statistics — RULES.md §5.2 (v2.1).
 * Shared by the server (authoritative standings totals) and the client
 * (local matches + effect triggers). Display-only: never affects ranking.
 */
import type { HexPos } from '../types/game.js';
import { hexDistance } from '../utils/hexMath.js';

export interface ComboStats {
  /** Total hop segments over the match (a single hop counts 1). */
  totalHops: number;
  /** Largest single-turn combo (≥ 2 = a true chain hop). */
  maxCombo: number;
  /** Turns with combo ≥ 2. */
  comboCount: number;
  /** totalHops×10 + Σ(combo≥2: combo²×5). */
  performance: number;
}

/**
 * A move's combo = number of hop segments in its authoritative path.
 * A step move (single segment of hex distance 1) has combo 0; hop
 * segments have distance 2, so no extra protocol field is needed.
 */
export function comboOfPath(path: readonly HexPos[]): number {
  let hops = 0;
  for (let i = 0; i + 1 < path.length; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (a && b && hexDistance(a, b) === 2) hops++;
  }
  return hops;
}

export function createComboStats(): ComboStats {
  return { totalHops: 0, maxCombo: 0, comboCount: 0, performance: 0 };
}

/** Fold one applied move into the seat accumulator. */
export function recordCombo(stats: ComboStats, combo: number): void {
  stats.totalHops += combo;
  stats.performance += combo * 10;
  if (combo > stats.maxCombo) stats.maxCombo = combo;
  if (combo >= 2) {
    stats.comboCount += 1;
    stats.performance += combo * combo * 5; // quadratic: rewards long-chain planning
  }
}
